import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isUrl } from 'sh-www/utils/misc';
import { enqueueTask } from 'ember-concurrency';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import { showModal } from 'sh-www/utils/modal-controls';
import { PROJECTDIGEST_VERIFICATION_STATUS } from 'sh-www/models/projectdigest-verification';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

const CLASS_NAME = 'SH-r-referral-receive-verify-digest';

class ReferralReceiveController extends Controller {
  queryParams = ['code'];

  @service api;
  @service('commands.projectdigest-commands') projectdigestCommands;
  @service session;
  @service store;
  @service('video-thumbnail-from-url') videoThumbnailService;

  isOverlayOpen = false;
  verificationStatus = 0;
  verificationFlowComplete = false;
  hasThumbnailFailed = false;
  hasThumbnailLoaded = false;

  get className() {
    return CLASS_NAME;
  }

  @computed('model.projectdigestVerification','verificationFlowComplete')
  get projectNeedsVerifying() {
    return !this.verificationFlowComplete
      && this.model.projectdigestVerification.status === PROJECTDIGEST_VERIFICATION_STATUS.PENDING;
  }

  @computed('model.projectdigestVerification.projectdigest')
  get projectDigest() {
    return this.model.projectdigestVerification.projectdigest;
  }

  @computed('projectDigest.{thumbnailUrl,link}')
  get thumbnailUrl() {
    const projectDigest = this.projectDigest;
    const thumbnail = projectDigest.thumbnailUrl;

    if (thumbnail) {
      return thumbnail;
    }

    const link = projectDigest.link;

    return this.handleGetThumbnail(link);
  }

  @computed('hasThumbnailFailed','thumbnailUrl','hasThumbnailLoaded')
  get hasVisibleThumbnail() {
    if (this.hasThumbnailFailed) return false;

    return this.thumbnailUrl && this.hasThumbnailLoaded;
  }

  /**
   * Store whether they approve or deny before saving the response text.
   *
   * @param {ProjectdigestVerification} projectdigestVerification
   * @param {PROJECTDIGEST_VERIFICATION_STATUS} status
   * @returns
   */
  @enqueueTask
  *submitInitialReponseTask(projectdigestVerification, status) {
    try {
      yield this.handleSubmitVerification(projectdigestVerification, {
        status,
      });
      this.setProperties({
        isOverlayOpen: true,
        verificationStatus: status,
      });
    } catch(error) {
      handleErrorResponseWithModal(this, error, 'Could not send your response at this time. Please reload and try again in a moment.');
    }
  }

  @action
  handleSubmitNote(projectdigestVerification, notes) {
    return this.api.post(`/referral/verification/${projectdigestVerification.id}/note`, {
      jsonData: {
        notes,
      },
    });
  }

  @action
  handleSubmitVerification(projectdigestVerification, {
    status,
   }) {
    return this.api.post(`/referral/verification/${projectdigestVerification.id}`, {
      jsonData: {
        status,
      },
    });
  }

  @action
  handleViewedReferralDeletedDigest(projectdigestVerification) {
    this.handleDecline(projectdigestVerification);
    this.set('isOverlayOpen', false);
  }

  @action
  findProjectdigestVerification(code) {
    const options = {
      params: {
        referral_token: code,
        include: 'projectdigest,projectdigest.services,projectdigest.user,projectdigest.user.location,projectdigest.last-location',
      }
    };

    return this.api.request('/referral/receive', options).then(response => {
      this.store.pushPayload(response);
      return this.store.peekRecord('projectdigestVerification', response.data.id);
    });
  }

  @action
  handleGetThumbnail(link) {
    return isUrl(link) ? this.videoThumbnailService.getThumb(link) : null;
  }

  @action
  handleThumbnailError() {
    this.set('hasThumbnailFailed', true);
  }

  @action
  handleThumbnailLoad() {
    this.set('hasThumbnailLoaded', true);
  }

  @action
  handleThumbnailClick(projectDigest) {
    showModal(this, LightboxOverlay, {
      digestsToShowForVideoCarousel: [projectDigest],
    });
  }

  @action
  handleApprove(projectdigestVerification) {
    this.submitInitialReponseTask.perform(projectdigestVerification, PROJECTDIGEST_VERIFICATION_STATUS.VERIFIED);
  }

  @action
  handleDecline(projectdigestVerification) {
    this.submitInitialReponseTask.perform(projectdigestVerification, PROJECTDIGEST_VERIFICATION_STATUS.CANNOT_VERIFY);
  }

  @action
  closeFeedbackOverlay() {
    this.set('isOverlayOpen', false);
  }

  @action
  afterSubmit() {
    this.set('verificationFlowComplete', true);
  }
}

export default ReferralReceiveController;
